import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  User,
  MapPin,
  CreditCard,
  Shield,
  Database,
  Clock,
  Phone,
} from "lucide-react";
import moment from "moment";

// Import the AdminUser type from UsersDataTable
import type { AdminUser } from "./UsersDataTable";
import {
  formatSubscriptionStatus,
  getSubscriptionStatusColor,
} from "@/utils/subscription-status";

interface UserInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AdminUser | null;
}

export const UserInfoDialog: React.FC<UserInfoDialogProps> = ({
  open,
  onOpenChange,
  user,
}) => {
  if (!user) return null;

  // Function to generate color based on plan name
  const generatePlanColor = (planName: string | null) => {
    if (!planName)
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";

    const cleanedPlan = planName.trim().toLowerCase();

    // Explicit mapping for common plans to ensure consistency
    const planColorMap: Record<string, string> = {
      starter:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
      pro: "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
      maxi: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400",
      premium:
        "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400",
      enterprise:
        "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
      basic: "bg-teal-100 text-teal-800 dark:bg-teal-900/20 dark:text-teal-400",
      advanced:
        "bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400",
      ultimate:
        "bg-violet-100 text-violet-800 dark:bg-violet-900/20 dark:text-violet-400",
    };

    // Check if we have an explicit mapping first
    if (planColorMap[cleanedPlan]) {
      return planColorMap[cleanedPlan];
    }

    // Fallback to hash-based generation for unknown plans
    let hash = 0;
    for (let i = 0; i < cleanedPlan.length; i++) {
      hash = cleanedPlan.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Generate color based on hash (excluding colors used for status badges: red, green, yellow)
    const colorMap = [
      "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400",
      "bg-stone-100 text-stone-800 dark:bg-stone-900/20 dark:text-stone-400",
      "bg-zinc-100 text-zinc-800 dark:bg-zinc-900/20 dark:text-zinc-400",
      "bg-slate-100 text-slate-800 dark:bg-slate-900/20 dark:text-slate-400",
      "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
      "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
      "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
      "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400",
    ];

    return colorMap[Math.abs(hash) % colorMap.length];
  };

  const formatDate = (dateString: string) => {
    return moment(dateString).format("DD/MM/YYYY HH:mm");
  };

  const formatSubscriptionEndDate = (dateString: string | undefined) => {
    if (!dateString) return "Non défini";
    return moment(dateString).format("DD/MM/YYYY");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1200px] max-h-[90vh] overflow-y-auto scrollbar-thin mx-4">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <User className="h-5 w-5" />
            <span>
              {user.userType === "company"
                ? user.companyName
                : `${user.firstName} ${user.lastName}`}
            </span>
          </DialogTitle>
          <DialogDescription>
            Informations détaillées de l'utilisateur
          </DialogDescription>
        </DialogHeader>

        {/* Header with Status and Verification */}
        <div className="flex items-center justify-between pb-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 flex-wrap gap-2">
            <Badge
              className={getSubscriptionStatusColor(user.subscriptionStatus)}
            >
              {formatSubscriptionStatus(user.subscriptionStatus)}
            </Badge>
            <Badge className={generatePlanColor(user.subscriptionPlan)}>
              {user.subscriptionPlan || "Non abonné"}
            </Badge>
            <Badge variant={user.isEmailVerified ? "default" : "destructive"}>
              <Shield className="h-3 w-3 mr-1" />
              {user.isEmailVerified ? "Email vérifié" : "Email non vérifié"}
            </Badge>
            <Badge variant={user.userType === "company" ? "blue" : "purple"}>
              {user.userType === "company" ? "Entreprise" : "Individu"}
            </Badge>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Créé le {formatDate(user.createdAt)}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {/* Carte 1: Informations personnelles */}
          <div className="space-y-4 flex flex-col">
            <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
              <User className="h-4 w-4 mr-2" />
              Informations personnelles
            </h4>
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg h-full">
              <div className="space-y-3">
                {user.userType === "company" ? (
                  <>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Nom de l'entreprise
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {user.companyName || "Non renseigné"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        SIRET
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {user.siret || "Non renseigné"}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Prénom
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {user.firstName || "Non renseigné"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Nom
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {user.lastName || "Non renseigné"}
                      </p>
                    </div>
                  </>
                )}
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Email
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white break-all">
                    {user.email}
                  </p>
                </div>
                {user.phone && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                      <Phone className="h-3 w-3 mr-1" />
                      Téléphone
                    </p>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {user.phone}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Carte 2: Abonnement */}
          <div className="space-y-4 flex flex-col">
            <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
              <CreditCard className="h-4 w-4 mr-2" />
              Abonnement
            </h4>
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg h-full">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Plan
                  </p>
                  <Badge className={generatePlanColor(user.subscriptionPlan)}>
                    {user.subscriptionPlan || "Non abonné"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Statut
                  </p>
                  <Badge
                    className={getSubscriptionStatusColor(
                      user.subscriptionStatus,
                    )}
                  >
                    {formatSubscriptionStatus(user.subscriptionStatus)}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Fin d'abonnement
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {formatSubscriptionEndDate(user.subscriptionEndDate)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Carte 3: Informations du compte */}
          <div className="space-y-4 flex flex-col">
            <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
              <Shield className="h-4 w-4 mr-2" />
              Compte
            </h4>
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg flex-grow">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Statut du compte
                  </p>
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-900 dark:text-white">
                        {user.isEmailVerified ? "Vérifié" : "Non vérifié"}
                      </span>
                      <Badge
                        className={
                          user.isEmailVerified
                            ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                            : "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                        }
                      >
                        {user.isEmailVerified ? "Vérifié" : "Non vérifié"}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center">
                    Membre depuis
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {moment(user.createdAt).fromNow()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Carte 4: Adresse (si disponible) */}
          {(user.address || user.city || user.postalCode) && (
            <div className="space-y-4 flex flex-col">
              <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                Adresse
              </h4>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg h-full">
                <div className="space-y-3">
                  {user.address && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Adresse
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {user.address}
                      </p>
                    </div>
                  )}
                  {user.city && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Ville
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {user.city}
                      </p>
                    </div>
                  )}
                  {user.postalCode && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Code postal
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {user.postalCode}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Carte 5: Informations Stripe */}
          {(user.customerId || user.subscriptionId) && (
            <div className="space-y-4 flex flex-col">
              <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
                <Database className="h-4 w-4 mr-2" />
                Stripe
              </h4>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg h-full">
                <div className="space-y-3">
                  {user.customerId && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Customer ID
                      </p>
                      <p className="font-mono text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 p-2 rounded border break-all">
                        {user.customerId}
                      </p>
                    </div>
                  )}
                  {user.subscriptionId && (
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        Subscription ID
                      </p>
                      <p className="font-mono text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 p-2 rounded border break-all">
                        {user.subscriptionId}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Carte 6: Informations techniques */}
          <div className="space-y-4 flex flex-col">
            <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              Technique
            </h4>
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg h-full">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    ID Utilisateur
                  </p>
                  <p className="text-xs font-mono text-gray-900 dark:text-white bg-white dark:bg-gray-900 p-2 rounded border break-all">
                    {user.id}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Email vérifié
                  </p>
                  <Badge
                    variant={user.isEmailVerified ? "default" : "destructive"}
                  >
                    <Shield className="h-3 w-3 mr-1" />
                    {user.isEmailVerified ? "Oui" : "Non"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Dernière MAJ
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {formatDate(user.updatedAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
